import {
  ArticleRounded as FilingIcon,
  AssignmentIndRounded as IncludeContextIcon,
  CheckRounded as CheckIcon,
  MoreVertRounded as MoreIcon,
  NoteAddRounded as NoteIcon,
  SearchRounded as SearchIcon,
  SendRounded as SendIcon,
  SmartToyRounded as AiIcon,
} from "@mui/icons-material";
import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { useState } from "react";

const MODES = {
  web_search: {
    label: "Web",
    icon: SearchIcon,
    placeholder: "Search the web for company info, news, filings...",
    actionLabel: "Search",
    actionIcon: SearchIcon,
    color: "primary",
  },
  ai_query: {
    label: "AI",
    icon: AiIcon,
    placeholder: "Ask the AI research agent a question...",
    actionLabel: "Ask",
    actionIcon: SendIcon,
    color: "secondary",
  },
  reference_filing: {
    label: "SEC filing",
    icon: FilingIcon,
    placeholder: "Ticker, company name, or CIK (e.g. TSLA, Tesla, 1318605)",
    actionLabel: "Fetch",
    actionIcon: FilingIcon,
    color: "info",
  },
};

const ResearchSearchBar = ({
  onSearch,
  onAddNote,
  onAddReferenceFiling,
  isSearching,
}) => {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("web_search");
  const [includeSubmissionContext, setIncludeSubmissionContext] = useState(
    false
  );
  const [overflowAnchor, setOverflowAnchor] = useState(null);

  const config = MODES[mode];
  const ActionIcon = config.actionIcon;

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    if (mode === "reference_filing") {
      onAddReferenceFiling(trimmed);
    } else {
      onSearch(trimmed, mode, {
        includeSubmissionContext:
          mode === "ai_query" && includeSubmissionContext,
      });
    }
    setQuery("");
  };

  const handleAddNote = () => {
    setOverflowAnchor(null);
    onAddNote();
  };

  return (
    <Stack spacing={1}>
      <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, val) => val && setMode(val)}
          size={"small"}
          fullWidth
          sx={{ flex: 1 }}
        >
          {Object.entries(MODES).map(([key, m]) => {
            if (key === "reference_filing" && !onAddReferenceFiling)
              return null;
            const Icon = m.icon;
            return (
              <ToggleButton
                key={key}
                value={key}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  py: 0.5,
                }}
              >
                <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
                  <Icon sx={{ fontSize: 16 }} />
                  <span>{m.label}</span>
                </Stack>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
        <Tooltip title={"More actions"} enterDelay={500}>
          <IconButton
            size={"small"}
            onClick={(e) => setOverflowAnchor(e.currentTarget)}
          >
            <MoreIcon fontSize={"small"} />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={overflowAnchor}
          open={Boolean(overflowAnchor)}
          onClose={() => setOverflowAnchor(null)}
        >
          <MenuItem onClick={handleAddNote}>
            <ListItemIcon>
              <NoteIcon fontSize={"small"} />
            </ListItemIcon>
            <ListItemText primary={"Add manual note"} />
          </MenuItem>
        </Menu>
      </Stack>

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          size={"small"}
          placeholder={config.placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={isSearching}
          sx={{
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${config.color}.main`,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position={"end"}>
                {isSearching ? (
                  <CircularProgress size={20} />
                ) : (
                  <Button
                    type={"submit"}
                    size={"small"}
                    disabled={!query.trim()}
                    variant={"contained"}
                    color={config.color}
                    startIcon={<ActionIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      py: 0.25,
                      px: 1,
                      minWidth: 0,
                    }}
                  >
                    {config.actionLabel}
                  </Button>
                )}
              </InputAdornment>
            ),
          }}
        />
      </form>

      {mode === "ai_query" && (
        <Tooltip
          title={
            "Share insured name, address, NAICS, claims and exposure data with this AI query"
          }
          enterDelay={500}
        >
          <Chip
            icon={
              includeSubmissionContext ? (
                <CheckIcon sx={{ fontSize: 14 }} />
              ) : (
                <IncludeContextIcon sx={{ fontSize: 14 }} />
              )
            }
            label={"Include submission context"}
            size={"small"}
            clickable
            onClick={() => setIncludeSubmissionContext((v) => !v)}
            color={includeSubmissionContext ? "primary" : "default"}
            variant={includeSubmissionContext ? "filled" : "outlined"}
            sx={{ alignSelf: "flex-start", fontSize: "0.7rem" }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};

export default ResearchSearchBar;
